import React, { useEffect, useState } from "react";
import { useConnectedUser } from "../../context/connected-user.context";
import { StakingInfo } from "../../model/round.model";
import { Button, Web3Button } from "../../design-system/button";
import { RoundStakingStatus } from "../../web3/model/staking.model";
import { toast } from "react-toastify";
import { NO_SALE_ROUND, StakingInfoAndUserStake } from "../../web3/contract";
import { Tooltip } from "../../design-system/tooltip";

export const UserStakingContainer: React.FC<{}> = () => {
    const [currentTab, setCurrentTab] = useState<"stake" | "unstake">("stake");

    return (
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center gap-4 mb-8 mt-6">
                <Button 
                    text="Stake" 
                    onClick={() => setCurrentTab("stake")} 
                    type={currentTab === "stake" ? 'unselected' : 'action'} 
                    // className="w-32 sm:w-40 py-3 text-lg"
                />
                <Button 
                    text="Unstake" 
                    onClick={() => setCurrentTab("unstake")} 
                    type={currentTab === "unstake" ? 'unselected' : 'action'}
                    // className="w-32 sm:w-40 py-3 text-lg" 
                />
            </div>
            <div className="transition-all duration-300">
                {currentTab === "stake" && <ListActivePublicStakingRounds />}
                {currentTab === "unstake" && <Unstake />}
            </div>
        </div>
    );
};

const ListActivePublicStakingRounds: React.FC<{}> = () => {
    const contract = useConnectedUser().contract;
    const [address] = useState<string | undefined>(useConnectedUser().connectedUser?.walletAddress)
    const [activeStakingRounds, setActiveStakingRounds] = useState<StakingInfoAndUserStake[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const retrieveAllActivePublicStakingRounds = async () => {
        if (contract && address) {
            setLoading(true);
            const activeStakingRounds = await contract.retrieveAllPublicActiveStakingRounds();
            const activeStakingRoundsWithUserStakes = await contract.retrieveUserStakes(address, activeStakingRounds)
            setActiveStakingRounds(activeStakingRoundsWithUserStakes);
            setLoading(false);
        }
    }

    useEffect(() => {
        retrieveAllActivePublicStakingRounds();
    }, [contract]);

    if (loading) {
        return (
            <div className="flex justify-center items-center p-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="mb-8">
                <h2        
                    className="text-3xl font-bold bg-clip-text text-transparent"
                    style={{
                    backgroundImage: 'linear-gradient(to right, #ff0000, #ff8c00)'
                    }}>Active Staking Rounds</h2>
                <p className="text-white-600 mt-2">Choose a round to stake your tokens</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activeStakingRounds.map((stakingInfo, index) => (
                    <StakingRoundCard 
                        key={index} 
                        stakingInfo={stakingInfo.stakingInfo}
                        userStakes={stakingInfo.countCDKStaked}
                    />
                ))}
            </div>

            {activeStakingRounds.length === 0 && (
                <div className="p-8 sm:p-12 text-center bg-gray-50 rounded-2xl shadow-sm border border-gray-100">
                    <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <h1 className="text-lg sm:text-xl font-medium text-gray-700">No staking rounds available</h1>
                    <p className="text-gray-500 mt-2">Check back later for new opportunities</p>
                </div>
            )}
        </div>
    );
}

interface StakingRoundCardProps {
    stakingInfo: StakingInfo;
    userStakes: number;
}

const StakingRoundCard: React.FC<StakingRoundCardProps> = ({ stakingInfo, userStakes }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // const status = 'ACTIVE'; // Replace with dynamic status if available

    // const getStatusIndicator = () => {
    //     switch (status) {
    //         case "ACTIVE":
    //             return {
    //                 badge: 'bg-emerald-100 text-emerald-800',
    //                 dot: 'bg-emerald-500',
    //                 text: 'ACTIVE'
    //             };
    //         default:
    //             return {
    //                 badge: 'bg-amber-100 text-amber-800',
    //                 dot: 'bg-amber-500',
    //                 text: 'PENDING'
    //             };
    //     }
    // };

    // const statusIndicator = getStatusIndicator();

    return (
        <div className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg border border-gray-100 flex flex-col h-full">
            {/* Card Header */}
            <div className="p-4 md:p-5 border-b border-gray-100">
                <div className="flex justify-between items-center">
                    <h3 className="text-sm md:text-base font-bold text-gray-800">Round {stakingInfo.stakingId}</h3>
                    {/* <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${statusIndicator.badge}`}>
                        <span className={`h-2 w-2 rounded-full ${statusIndicator.dot}`}></span>
                        <span className="text-xs font-semibold">{statusIndicator.text}</span>
                    </div> */}
                </div>
            </div>

            {/* Card Body */}
            <div className="px-4 md:px-5 py-4 flex-grow">
                <div className="flex flex-col gap-3">
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Rewards:</span>
                        <span className="font-semibold text-gray-900">{stakingInfo.stakingDetails.stakingRewardPercentage}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Cliff Period:</span>
                        <span className="font-semibold text-gray-900">{stakingInfo.stakingDetails.cliffPeriod} months</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Vesting Period:</span>
                        <span className="font-semibold text-gray-900">{stakingInfo.stakingDetails.vestingPeriod} months</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">My staked CDK:</span>
                        <span className="font-semibold text-gray-900">{userStakes}</span>
                    </div>
                </div>
            </div>

            {/* Card Footer */}
            <div className="p-4 md:p-5 bg-gray-50 border-t border-gray-100">
                {!isExpanded ? (
                    <Button 
                        onClick={() => setIsExpanded(true)}
                        type="gradient"         
                        gradientColors={['#ff0000', '#ff8c00']}
                        text="Stake in this round"
                    >       
                    </Button>
                ) : (
                    <div className="space-y-4 rounded-2xl border border-orange-200 bg-orange-50 p-5 shadow-md">
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold text-base text-orange-800">Stake in Round</h4>
                            <button 
                                onClick={() => setIsExpanded(false)} 
                                className="p-1 rounded-full bg-orange-100 hover:bg-orange-200 text-orange-600 transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>

                        <StakeInRound stakingRound={stakingInfo} onClose={() => setIsExpanded(false)} />
                    </div>
                )}
            </div>
        </div>
    );
};

type StakeRoundProps = {
    stakingRound: StakingInfo;
    onClose: () => void;
};

const StakeInRound: React.FC<StakeRoundProps> = ({ stakingRound, onClose }) => {
    const connectedUserCtx = useConnectedUser();
    const connectedUserData = useConnectedUser().connectedUser;
    const contract = useConnectedUser().contract;
    const [amountToStake, setAmountToStake] = useState<number>(0);
    const [canStake, setCanStake] = useState<boolean>(false);

    useEffect(() => {
        if (amountToStake > 0 
            && connectedUserData?.cdkBalance 
            && amountToStake <= connectedUserData?.cdkBalance) {
            setCanStake(true);
        } else {
            setCanStake(false);
        }
    }, [amountToStake, connectedUserData?.cdkBalance]);

    const handlePercentageClick = (percentage: number) => {
        if (connectedUserData?.cdkBalance !== undefined) {
            const calculatedAmount = (connectedUserData.cdkBalance * percentage) / 100;
            setAmountToStake(calculatedAmount);
        }
    };

    const handleStake = async () => {
        if (contract && connectedUserData && stakingRound) {
            await contract.doStakeTokens(stakingRound.stakingId, amountToStake);
            connectedUserCtx.reloadAllBalances();       
        }
    }

    const handleStakeSuccess = () => {
        toast.success('Staked successfully');
        setAmountToStake(0);
        onClose();
    }

    return (
        <div className="space-y-4">
            
            <div className="flex-1 relative">
                <input
                    type="number"
                    className="block w-full pl-3 pr-12 py-2.5 text-base border border-orange-300 rounded-lg focus:ring-orange-500 focus:border-orange-500 transition-all"
                    style={{ backgroundColor: "#ffffff" }}
                    value={amountToStake}
                    onChange={(e) => setAmountToStake(Number(e.target.value))}
                    placeholder="Enter amount to stake"
                    min="0"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">CDK</span>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-500">
                <span>Balance: {connectedUserData?.cdkBalance} $CDK</span>
            </div>

            <div className="grid grid-cols-4 gap-2">
                {[25, 50, 75, 100].map((percentage) => (
                    <button
                        key={percentage}
                        className="px-2 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-sm"
                        onClick={() => handlePercentageClick(percentage)}
                    >
                        {percentage}%
                    </button>
                ))}
            </div>

            <Web3Button
                text={`Stake ${amountToStake} $CDK`}
                web3Action={handleStake}
                onSuccess={handleStakeSuccess}
                disabled={!canStake}
            />
        </div>
    );
};

const Unstake: React.FC<{}> = () => {
    const contract = useConnectedUser().contract;
    const [address] = useState<string | undefined>(useConnectedUser().connectedUser?.walletAddress);
    const [activeStakingRounds, setActiveStakingRounds] = useState<StakingInfoAndUserStake[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const retrieveAllActivePublicStakingRounds = async () => {
        if (contract && address) {
            setLoading(true);
            const activeStakingRounds = await contract.retrieveAllPublicActiveUnStakingRounds();
            const activeStakingRoundsWithUserStakes = await contract.retrieveUserStakes(address, activeStakingRounds);
            setActiveStakingRounds(activeStakingRoundsWithUserStakes);
            setLoading(false);
        }
    }

    useEffect(() => {
        retrieveAllActivePublicStakingRounds();
    }, [contract]);

    // Filter out rounds where both stakes and rewards are 0
    const filteredStakingRounds = activeStakingRounds.filter(stakingInfo => 
        stakingInfo.countCDKStaked > 0 || stakingInfo.rewards > 0
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center p-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="mb-8">
                <h2        
                    className="text-3xl font-bold bg-clip-text text-transparent"
                    style={{
                    backgroundImage: 'linear-gradient(to right, #ff0000, #ff8c00)'
                    }}>Your Staked Tokens</h2>
                <p className="text-white-600 mt-2">Manage your staked tokens across rounds</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredStakingRounds.map((stakingInfo, index) => (
                    <UnstakeRoundCard
                        key={index}
                        stakingInfo={stakingInfo.stakingInfo}
                        userStakes={stakingInfo.countCDKStaked}
                        rewards={stakingInfo.rewards}
                    />
                ))}
            </div>

            {filteredStakingRounds.length === 0 && (
                <div className="p-8 sm:p-12 text-center bg-gray-50 rounded-2xl shadow-sm border border-gray-100">
                    <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <h1 className="text-lg sm:text-xl font-medium text-gray-700">No staked tokens found</h1>
                    <p className="text-gray-500 mt-2">You don't have any staked tokens or rewards to withdraw</p>
                </div>
            )}
        </div>
    );
};

interface UnstakeRoundCardProps {
    stakingInfo: StakingInfo;
    userStakes: number;
    rewards: number;
}

const UnstakeRoundCard: React.FC<UnstakeRoundCardProps> = ({ stakingInfo, userStakes, rewards }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg border border-gray-100 flex flex-col h-full">
            {/* Card Header */}
            <div className="p-4 md:p-5 border-b border-gray-100">
                <div className="flex justify-between items-center">
                    <h3 className="text-sm md:text-base font-bold text-gray-800">Round {stakingInfo.stakingId}</h3>
                </div>
            </div>

            {/* Card Body */}
            <div className="px-4 md:px-5 py-4 flex-grow">
                <div className="flex flex-col gap-3">
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Rewards Rate:</span>
                        <span className="font-semibold text-gray-900">{stakingInfo.stakingDetails.stakingRewardPercentage}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Cliff Period:</span>
                        <span className="font-semibold text-gray-900">{stakingInfo.stakingDetails.cliffPeriod} months</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Vesting Period:</span>
                        <span className="font-semibold text-gray-900">{stakingInfo.stakingDetails.vestingPeriod} months</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">My staked CDK:</span>
                        <span className="font-semibold text-gray-900">{userStakes}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Available Rewards:</span>
                        <span className="font-semibold text-green-600">{rewards.toFixed(4)} CDK</span>
                    </div>
                </div>
            </div>

            {/* Card Footer */}
            <div className="p-4 md:p-5 bg-gray-50 border-t border-gray-100">
                {!isExpanded ? (
                    <Button 
                        onClick={() => setIsExpanded(true)}
                        type="gradient"         
                        gradientColors={['#ff0000', '#ff8c00']}
                        text="Unstake from this round"
                    >       
                    </Button>
                ) : (
                    <div className="space-y-4 rounded-2xl border border-orange-200 bg-orange-50 p-5 shadow-md">
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold text-base text-orange-800">Unstake from Round</h4>
                            <button 
                                onClick={() => setIsExpanded(false)} 
                                className="p-1 rounded-full bg-orange-100 hover:bg-orange-200 text-orange-600 transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>

                        <UnstakeOperation stakingRoundId={stakingInfo.stakingId} />
                    </div>
                )}
            </div>
        </div>
    );
};

const UnstakeOperation: React.FC<{stakingRoundId: string}> = ({ stakingRoundId }) => {
    const connectedUserCtx = useConnectedUser();
    const contract = useConnectedUser().contract;
    const userAddress = useConnectedUser().connectedUser?.walletAddress;
    const [canUnstake, setCanUnstake] = useState<boolean>(false);
    const [withdrawableAmount, setWithdrawableAmount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);

    const computeRewardAndStatus = async () => {
        if (contract && userAddress) {
            setLoading(true);
            const withdrawableAmount = await contract.stakingFacet.getWithdrawableAmount(stakingRoundId, userAddress);
            const status = await contract.stakingFacet.getStakingStatus(stakingRoundId);
            setWithdrawableAmount(withdrawableAmount);
            setCanUnstake(withdrawableAmount > 0 && Number(status) === Number(RoundStakingStatus.ON));
            setLoading(false);
        }
    }

    const withdraw = async () => {
        if (contract && userAddress) {
            await contract.stakingFacet.doWithdrawTokens(stakingRoundId, userAddress);
            connectedUserCtx!.reloadAllBalances();
            toast.success('Withdrawn successfully');
        }
    }

    useEffect(() => {
        computeRewardAndStatus();
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center items-center p-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-orange-500"></div>
            </div>
        );
    }

    return (
        <div className="flex flex-col space-y-4">
            <div className="flex flex-col sm:flex-row justify-between sm:items-center">
                <span className="text-gray-700 mb-1 sm:mb-0">Withdrawable Amount:</span>
                <span className="text-xl font-bold text-orange-600">{withdrawableAmount} CDK</span>
            </div>

            {canUnstake ? (
                <Web3Button 
                    text="Withdraw Tokens" 
                    web3Action={withdraw} 
                    disabled={!canUnstake}
                    // className="w-full sm:w-auto sm:ml-auto py-3 text-base font-medium"
                />
            ) : (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-center">
                    No tokens available for withdrawal at this time
                </div>
            )}
        </div>
    );
};